/**
 * Enhanced Error Boundary with better error categorization and user-friendly messages
 */
import { AlertTriangle, Home, RefreshCw, AlertCircle } from 'lucide-react';
import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import { logger } from '../lib/utils/logger';
import type { AppError } from '../utils/error-utils';
import { APP_ROUTES, appUrl } from '../config/routes';
import {
  categorizeError,
  formatErrorForLogging,
  getUserFriendlyMessage,
  isRecoverableError,
} from '../utils/error-utils';

interface Props {
  children: ReactNode;
  fallback?: (error: AppError, retry: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: AppError;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    const appError = categorizeError(error);
    return {
      hasError: true,
      error: appError,
    };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const appError = categorizeError(error);

    // Log error with appropriate level
    logger.error(formatErrorForLogging(appError), {
      error,
      errorInfo,
      componentStack: errorInfo.componentStack,
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Update state with error info
    this.setState({
      errorInfo,
    });
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  private handleGoHome = () => {
    window.location.href = appUrl(APP_ROUTES.DASHBOARD);
  };

  public override render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry);
      }

      const { error } = this.state;
      const message = getUserFriendlyMessage(error);
      const showRetry = isRecoverableError(error);

      return (
        <div className="min-h-screen flex items-center justify-center surface-secondary p-4">
          <div className="bg-white dark:bg-zinc-800 rounded-lg shadow-md border border-zinc-200 dark:border-zinc-700 max-w-md w-full">
            <div className="p-4 text-center">
              {/* Error Icon */}
              <div className="flex justify-center mb-4">
                <div className="rounded-full bg-red-100 dark:bg-red-900/20 p-3">
                  <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
                </div>
              </div>

              {/* Error Title */}
              <h4 className="text-2xl font-bold text-zinc-900 dark:text-white mb-2">
                {(() => {
                  switch (error.type) {
                    case 'AUTHENTICATION':
                      return 'Authentication Required';
                    case 'AUTHORIZATION':
                      return 'Access Denied';
                    case 'NETWORK':
                      return 'Connection Problem';
                    case 'NOT_FOUND':
                      return 'Not Found';
                    case 'SERVER':
                      return 'Server Error';
                    default:
                      return 'Something went wrong';
                  }
                })()}
              </h4>

              {/* Error Message */}
              <p className="text-base text-zinc-600 dark:text-zinc-400 mb-6">{message}</p>

              {/* Error Details (Development Only) */}
              {import.meta.env.DEV && error.details && (
                <div className="mb-4 text-left px-4 py-3 rounded-lg bg-zinc-50 dark:bg-zinc-900/20 border border-zinc-200 dark:border-zinc-700">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-zinc-600 dark:text-zinc-400 shrink-0 mt-0.5" />
                    <details className="flex-1">
                      <summary className="cursor-pointer text-sm font-medium text-zinc-900 dark:text-white">
                        Technical Details
                      </summary>
                      <pre className="mt-2 text-xs overflow-auto whitespace-pre-wrap select-text text-zinc-700 dark:text-zinc-300">
                        {error.details}
                        {this.state.errorInfo?.componentStack && (
                          <>
                            {'\n\nComponent Stack:'}
                            {this.state.errorInfo.componentStack}
                          </>
                        )}
                      </pre>
                    </details>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                {showRetry && (
                  <button
                    onClick={this.handleRetry}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2
                               bg-sky-600 dark:bg-sky-500 text-white rounded-lg
                               hover:bg-sky-700 dark:hover:bg-sky-600
                               focus:outline-hidden focus:ring-2 focus:ring-sky-500
                               transition-colors font-medium"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Try Again
                  </button>
                )}

                <button
                  onClick={this.handleGoHome}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2
                             border-2 border-zinc-300 dark:border-zinc-600
                             text-zinc-700 dark:text-zinc-300 bg-white dark:bg-zinc-800 rounded-lg
                             hover:bg-zinc-50 dark:hover:bg-zinc-700
                             focus:outline-hidden focus:ring-2 focus:ring-zinc-300
                             transition-colors font-medium"
                >
                  <Home className="h-4 w-4" />
                  Go Home
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
